import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Search, ChevronRight, X, Mail,
  Phone, MapPin, Building2, Eye, Trash2, Edit2,
  UsersRound, Globe
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

const STYLES = `
  .mc-header {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 24px; gap: 16px;
  }
  .mc-search-row {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 20px;
  }
  .mc-search-wrap {
    position: relative; flex: 1; max-width: 360px;
  }
  .mc-search-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25); pointer-events: none;
  }
  .mc-search {
    width: 100%; padding: 9px 14px 9px 38px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; transition: border-color 0.2s;
  }
  .mc-search:focus { border-color: rgba(14,165,233,0.35); }
  .mc-search::placeholder { color: rgba(255,255,255,0.20); }

  /* Client grid */
  .mc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
  }
  .mc-client-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; overflow: hidden;
    transition: border-color 0.2s, transform 0.2s;
    cursor: pointer;
  }
  .mc-client-card:hover {
    border-color: rgba(14,165,233,0.20);
    transform: translateY(-2px);
  }
  .mc-client-card-top {
    padding: 20px 20px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    display: flex; align-items: flex-start; gap: 14px;
  }
  .mc-client-avatar {
    width: 44px; height: 44px; border-radius: 12px;
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 20px; color: #fff; flex-shrink: 0;
    letter-spacing: 0.05em;
  }
  .mc-client-name {
    font-size: 14px; font-weight: 700;
    color: #F1F5F9; margin-bottom: 4px;
  }
  .mc-client-contact {
    font-size: 11px; color: rgba(255,255,255,0.35);
    display: flex; align-items: center; gap: 5px;
  }
  .mc-client-meta {
    padding: 14px 20px;
    display: flex; flex-direction: column; gap: 7px;
  }
  .mc-meta-row {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: rgba(255,255,255,0.38);
  }
  .mc-meta-row svg { flex-shrink: 0; }
  .mc-client-footer {
    padding: 12px 20px;
    border-top: 1px solid rgba(255,255,255,0.05);
    display: flex; align-items: center; gap: 8px;
  }
  .mc-count-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 20px;
    font-size: 10px; font-weight: 700;
    font-family: 'DM Mono', monospace;
  }
  .mc-count-chip-blue {
    background: rgba(14,165,233,0.10);
    color: #38BDF8;
    border: 1px solid rgba(14,165,233,0.18);
  }
  .mc-count-chip-gold {
    background: rgba(245,158,11,0.10);
    color: #FCD34D;
    border: 1px solid rgba(245,158,11,0.18);
  }
  .mc-footer-arrow {
    margin-left: auto;
    color: rgba(255,255,255,0.18);
    transition: color 0.2s, transform 0.2s;
  }
  .mc-client-card:hover .mc-footer-arrow {
    color: rgba(14,165,233,0.60);
    transform: translateX(3px);
  }

  /* Detail panel (right slide-in) */
  .mc-detail-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(4px);
    z-index: 200;
    animation: fadeIn 0.15s ease;
  }
  .mc-detail-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 480px; max-width: 100vw;
    background: #0D1120;
    border-left: 1px solid rgba(255,255,255,0.08);
    box-shadow: -24px 0 80px rgba(0,0,0,0.50);
    z-index: 201;
    display: flex; flex-direction: column;
    animation: slideIn 0.22s cubic-bezier(0.4,0,0.2,1);
    overflow: hidden;
  }
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .mc-detail-header {
    padding: 22px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 14px;
    flex-shrink: 0;
  }
  .mc-detail-avatar {
    width: 48px; height: 48px; border-radius: 14px;
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px; color: #fff; flex-shrink: 0;
  }
  .mc-detail-name {
    font-size: 17px; font-weight: 800;
    color: #F1F5F9; flex: 1;
  }
  .mc-detail-body {
    flex: 1; overflow-y: auto; padding: 22px 24px;
  }
  .mc-detail-section {
    margin-bottom: 24px;
  }
  .mc-detail-section-title {
    font-size: 9px; font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: rgba(255,255,255,0.25);
    font-family: 'DM Mono', monospace;
    margin-bottom: 12px;
  }
  .mc-detail-info-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 13px; color: rgba(255,255,255,0.60);
  }
  .mc-detail-info-row:last-child { border-bottom: none; }
  .mc-detail-info-label {
    width: 100px; flex-shrink: 0;
    font-size: 10px; font-weight: 700;
    color: rgba(255,255,255,0.25);
    font-family: 'DM Mono', monospace;
    text-transform: uppercase; letter-spacing: 0.10em;
  }

  /* Site list in detail */
  .mc-site-item {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    margin-bottom: 8px;
  }
  .mc-site-icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(245,158,11,0.10);
    display: flex; align-items: center; justify-content: center;
    color: #FCD34D; flex-shrink: 0;
  }
  .mc-site-name { font-size: 13px; font-weight: 600; color: #E2E8F0; }
  .mc-site-loc  { font-size: 11px; color: rgba(255,255,255,0.30); }

  /* User list in detail */
  .mc-user-item {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 14px; border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    margin-bottom: 8px;
  }
  .mc-user-avatar {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(14,165,233,0.12);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; color: #38BDF8;
    flex-shrink: 0;
  }
  .mc-user-name { font-size: 13px; font-weight: 600; color: #E2E8F0; }
  .mc-user-email { font-size: 11px; color: rgba(255,255,255,0.30); }

  /* Form modal */
  .mc-form-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }
  .mc-form-full { grid-column: 1 / -1; }
`;

// ── Helpers ────────────────────────────────────────────────────────
const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const EMPTY_FORM = {
  client_name: '', contact_name: '', contact_email: '',
  contact_phone: '', address: '',
};

// ── Main Component ─────────────────────────────────────────────────
const MideastClients = () => {
  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null); // detail panel
  const [detailData,   setDetailData]   = useState(null);
  const [detailLoading,setDetailLoading]= useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [editClient,   setEditClient]   = useState(null);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  // ── Fetch all clients ──────────────────────────────────────────
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE}/mideast/clients`, getAuthHeader());
      setClients(res.data.clients || []);
    } catch (err) {
      console.error('[Clients] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ── Fetch single client detail ─────────────────────────────────
  const openDetail = useCallback(async (client) => {
    setSelected(client);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/mideast/clients/${client.id}`,
        getAuthHeader()
      );
      setDetailData(res.data);
    } catch (err) {
      console.error('[Clients] detail error:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ── Open create form ───────────────────────────────────────────
  const openCreate = () => {
    setEditClient(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  // ── Open edit form ─────────────────────────────────────────────
  const openEdit = (client, e) => {
    e.stopPropagation();
    setEditClient(client);
    setForm({
      client_name:   client.client_name   || '',
      contact_name:  client.contact_name  || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      address:       client.address       || '',
    });
    setError('');
    setShowForm(true);
  };

  // ── Save (create or update) ────────────────────────────────────
  const handleSave = async () => {
    if (!form.client_name.trim()) {
      setError('Client name is required');
      return;
    }
    setSaving(true); setError('');
    try {
      if (editClient) {
        await axios.put(
          `${BASE}/mideast/clients/${editClient.id}`,
          form, getAuthHeader()
        );
      } else {
        await axios.post(
          `${BASE}/mideast/clients`,
          form, getAuthHeader()
        );
      }
      setShowForm(false);
      fetchClients();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = async (client, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${client.client_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BASE}/mideast/clients/${client.id}`, getAuthHeader());
      fetchClients();
      if (selected?.id === client.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  // ── Filtered list ─────────────────────────────────────────────
  const filtered = clients.filter(c =>
    c.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{STYLES}</style>

      {/* Header */}
      <div className="mc-header">
        <div>
          <div className="md-page-title">Clients</div>
          <div className="md-page-sub">{clients.length} Mideast client{clients.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="md-btn md-btn-primary" onClick={openCreate}>
          <Plus size={14}/> Add Client
        </button>
      </div>

      {/* Search */}
      <div className="mc-search-row">
        <div className="mc-search-wrap">
          <Search size={14} className="mc-search-icon"/>
          <input
            className="mc-search"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="mc-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="md-skeleton"
              style={{ height: 180, borderRadius: 14 }}/>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="md-empty">
          <div className="md-empty-icon"><Users size={28}/></div>
          {search ? 'No clients match your search' : 'No clients yet — add your first one'}
        </div>
      ) : (
        <div className="mc-grid">
          {filtered.map(client => (
            <div
              key={client.id}
              className="mc-client-card"
              onClick={() => openDetail(client)}
            >
              <div className="mc-client-card-top">
                <div className="mc-client-avatar">
                  {getInitials(client.client_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mc-client-name">{client.client_name}</div>
                  {client.contact_name && (
                    <div className="mc-client-contact">
                      <UsersRound size={10}/> {client.contact_name}
                    </div>
                  )}
                </div>
                {/* Edit / Delete */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    className="md-btn md-btn-ghost"
                    style={{ padding: '5px 8px' }}
                    onClick={e => openEdit(client, e)}
                  >
                    <Edit2 size={12}/>
                  </button>
                  <button
                    className="md-btn md-btn-danger"
                    style={{ padding: '5px 8px' }}
                    onClick={e => handleDelete(client, e)}
                  >
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>

              <div className="mc-client-meta">
                {client.contact_email && (
                  <div className="mc-meta-row">
                    <Mail size={12}/> {client.contact_email}
                  </div>
                )}
                {client.contact_phone && (
                  <div className="mc-meta-row">
                    <Phone size={12}/> {client.contact_phone}
                  </div>
                )}
                {client.address && (
                  <div className="mc-meta-row">
                    <Globe size={12}/>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {client.address}
                    </span>
                  </div>
                )}
              </div>

              <div className="mc-client-footer">
                <span className="mc-count-chip mc-count-chip-gold">
                  <MapPin size={9}/> {client.site_count ?? 0} sites
                </span>
                <span className="mc-count-chip mc-count-chip-blue">
                  <Users size={9}/> {client.user_count ?? 0} users
                </span>
                <ChevronRight size={14} className="mc-footer-arrow"/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Detail Panel ─────────────────────────────────────── */}
      {selected && (
        <>
          <div className="mc-detail-overlay" onClick={() => setSelected(null)}/>
          <div className="mc-detail-panel">
            <div className="mc-detail-header">
              <div className="mc-detail-avatar">
                {getInitials(selected.client_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mc-detail-name">{selected.client_name}</div>
                {selected.contact_name && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                    {selected.contact_name}
                  </div>
                )}
              </div>
              <button
                className="md-modal-close"
                onClick={() => setSelected(null)}
              >
                <X size={18}/>
              </button>
            </div>

            <div className="mc-detail-body">
              {detailLoading ? (
                <>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="md-skeleton"
                      style={{ height: 16, marginBottom: 14, width: `${55 + i * 10}%` }}/>
                  ))}
                </>
              ) : detailData ? (
                <>
                  {/* Contact info */}
                  <div className="mc-detail-section">
                    <div className="mc-detail-section-title">Contact Information</div>
                    {[
                      { label: 'Email',  value: detailData.client.contact_email, icon: <Mail size={12}/> },
                      { label: 'Phone',  value: detailData.client.contact_phone, icon: <Phone size={12}/> },
                      { label: 'Address',value: detailData.client.address,        icon: <Globe size={12}/> },
                    ].filter(r => r.value).map(row => (
                      <div key={row.label} className="mc-detail-info-row">
                        <span className="mc-detail-info-label">{row.label}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {row.icon} {row.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Sites */}
                  <div className="mc-detail-section">
                    <div className="mc-detail-section-title">
                      Sites ({detailData.sites?.length || 0})
                    </div>
                    {detailData.sites?.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
                        No sites added yet
                      </div>
                    ) : detailData.sites?.map(site => (
                      <div key={site.id} className="mc-site-item">
                        <div className="mc-site-icon">
                          <MapPin size={14}/>
                        </div>
                        <div>
                          <div className="mc-site-name">{site.site_name}</div>
                          {site.location && (
                            <div className="mc-site-loc">{site.location}</div>
                          )}
                        </div>
                        {site.system_size && (
                          <div className="md-badge md-badge-gold" style={{ marginLeft: 'auto' }}>
                            {site.system_size}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Portal users */}
                  <div className="mc-detail-section">
                    <div className="mc-detail-section-title">
                      Portal Users ({detailData.users?.length || 0})
                    </div>
                    {detailData.users?.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
                        No users linked yet
                      </div>
                    ) : detailData.users?.map(user => (
                      <div key={user.id} className="mc-user-item">
                        <div className="mc-user-avatar">
                          {`${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase()}
                        </div>
                        <div>
                          <div className="mc-user-name">
                            {user.firstname} {user.lastname}
                          </div>
                          <div className="mc-user-email">{user.email}</div>
                        </div>
                        <div className="md-badge md-badge-blue" style={{ marginLeft: 'auto' }}>
                          {user.type}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      {showForm && (
        <div className="md-modal-overlay">
          <div className="md-modal">
            <div className="md-modal-header">
              <Building2 size={16} color="rgba(14,165,233,0.70)" strokeWidth={1.8}/>
              <div className="md-modal-title">
                {editClient ? 'Edit Client' : 'New Client'}
              </div>
              <button className="md-modal-close" onClick={() => setShowForm(false)}>
                <X size={18}/>
              </button>
            </div>

            <div className="md-modal-body">
              {error && (
                <div className="md-alert md-alert-error">⚠ {error}</div>
              )}
              <div className="mc-form-grid">
                <div className="md-field mc-form-full">
                  <label className="md-label">Client Name *</label>
                  <input
                    className="md-input"
                    placeholder="e.g. Al Reem Energy"
                    value={form.client_name}
                    onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                  />
                </div>
                <div className="md-field">
                  <label className="md-label">Contact Name</label>
                  <input
                    className="md-input"
                    placeholder="Primary contact"
                    value={form.contact_name}
                    onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  />
                </div>
                <div className="md-field">
                  <label className="md-label">Contact Phone</label>
                  <input
                    className="md-input"
                    placeholder="+971 50 000 0000"
                    value={form.contact_phone}
                    onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                  />
                </div>
                <div className="md-field mc-form-full">
                  <label className="md-label">Contact Email</label>
                  <input
                    className="md-input"
                    type="email"
                    placeholder="contact@company.com"
                    value={form.contact_email}
                    onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  />
                </div>
                <div className="md-field mc-form-full">
                  <label className="md-label">Address</label>
                  <input
                    className="md-input"
                    placeholder="City, Country"
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="md-modal-footer">
              <button
                className="md-btn md-btn-ghost"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="md-btn md-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? <><span className="md-spinner"/> Saving…</>
                  : editClient ? 'Save Changes' : 'Create Client'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MideastClients;